import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { t } from 'i18next';
import { useDownloadsTableStore } from '@store';
import { TruncatedTextWithConditionalTooltip } from '@components';
import {
  DownloadsStatus,
  IDownloadsTableEntry,
  IDownloadsTableFilters,
} from '@views/pages/Downloads/hooks';
import LinearProgress from '@mui/material/LinearProgress';
import { fileSizeLabelFormat } from '@util/fileSizeLabelFormat.ts';
import {
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';

export const getStatusIcon = (
  status: DownloadsStatus,
  download_percentage: number,
) => {
  switch (status) {
    case 'completed':
      return (
        <Chip
          sx={{
            backgroundColor: theme => theme.palette.success.main,
            color: theme => theme.palette.common.white,
            height: '24px',
            textTransform: 'capitalize',
          }}
          label={t('downloads.table.row.generated')}
        />
      );
    case 'queued':
      return (
        <Chip
          sx={{
            backgroundColor: theme => theme.palette.warning.main,
            color: theme => theme.palette.common.white,
            height: '24px',
            textTransform: 'capitalize',
          }}
          label={t('downloads.table.row.queued')}
        />
      );
    case 'failed':
      return (
        <Chip
          sx={{
            backgroundColor: theme => theme.palette.error.main,
            color: theme => theme.palette.common.white,
            height: '24px',
            textTransform: 'capitalize',
          }}
          label={t('downloads.table.row.failed')}
        />
      );
    default:
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '3fr 1fr',
            alignItems: 'center',
            width: '100%',
            gap: 1,
          }}
        >
          <LinearProgress variant='determinate' value={download_percentage} />
          <Typography variant='body2'>
            {`${download_percentage || 0}%`}
          </Typography>
        </Box>
      );
  }
};

interface UseDownloadsTableParams {
  rows: IDownloadsTableEntry[];
  filters: IDownloadsTableFilters;
  onPageIndexChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
  setRowsSelection: (selectedIds: Set<string>) => void;
  handleDownload: (row: IDownloadsTableEntry) => void;
  handleCancel: (row: IDownloadsTableEntry) => void;
  handleRetry: (row: IDownloadsTableEntry) => void;
  isDeleting: boolean;
  isMultipleDownloads: boolean;
  currentDownloadItem: IDownloadsTableEntry | null;
}

export const useDownloadsTable = ({
  rows,
  filters,
  onPageIndexChange,
  onPageSizeChange,
  setRowsSelection,
  handleDownload,
  handleCancel,
  handleRetry,
  isDeleting,
  isMultipleDownloads,
  currentDownloadItem,
}: UseDownloadsTableParams) => {
  const density = useDownloadsTableStore(state => state.density);
  const setDensity = useDownloadsTableStore(state => state.setDensity);
  const resetView = useDownloadsTableStore(state => state.resetView);

  const selectedIds = useMemo(
    () => new Set<string>(rows.filter(r => r.checked).map(r => r.id)),
    [rows],
  );

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: selectedIds }),
    [selectedIds],
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page: filters.page - 1, pageSize: filters.limit }),
    [filters.page, filters.limit],
  );

  const columns = useMemo<GridColDef<IDownloadsTableEntry>[]>(
    () => [
      {
        field: 'name',
        headerName: t('downloads.table.header.fileName'),
        flex: 1,
        minWidth: 240,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
              maxWidth: '100%',
            }}
          >
            <TruncatedTextWithConditionalTooltip
              width='100%'
              variant='body2'
              sx={{ fontSize: '14px' }}
              text={params.row.name}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                width='100%'
                variant='caption'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                text={
                  params.row.file_size
                    ? fileSizeLabelFormat(params.row.file_size)
                    : '-'
                }
              />
            )}
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: t('downloads.table.header.status'),
        flex: 1,
        minWidth: 220,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              px: 2,
            }}
          >
            {getStatusIcon(params.row.status, params.row.download_percentage)}
          </Box>
        ),
      },
      {
        field: 'generated_at',
        headerName: t('downloads.table.header.generatedOn'),
        width: 180,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params =>
          params.row.generated_at ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <TruncatedTextWithConditionalTooltip
                width='100%'
                text={dayjs(params.row.generated_at).format('MMM D, YYYY')}
                sx={{ fontSize: '14px' }}
              />
              {density !== 'dense' && (
                <TruncatedTextWithConditionalTooltip
                  width='100%'
                  text={dayjs(params.row.generated_at).format('hh:mm')}
                  variant='body2'
                  color='textSecondary'
                  sx={{ fontSize: '12px' }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              -
            </Box>
          ),
      },
      {
        field: 'action',
        headerName: '',
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const row = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {row.status === 'completed' && (
                <Button
                  sx={{
                    fontWeight: 'regular',
                    fontSize: '14px',
                    color: 'white',
                    padding: '4px 10px',
                  }}
                  onClick={() => handleDownload(row)}
                  disabled={
                    currentDownloadItem?.id === row.id ||
                    (isDeleting && row.checked) ||
                    (isMultipleDownloads && row.checked)
                  }
                  color='secondary'
                  variant='contained'
                >
                  {t('downloads.download')}
                </Button>
              )}
              {(row.status === 'queued' || row.status === 'in_progress') && (
                <Button
                  sx={{
                    fontWeight: 'regular',
                    fontSize: '14px',
                    padding: '4px 10px',
                  }}
                  disabled={
                    currentDownloadItem?.id === row.id ||
                    (isDeleting && row.checked)
                  }
                  onClick={() => handleCancel(row)}
                  variant='outlined'
                  color='primary'
                >
                  {t('general.cancel')}
                </Button>
              )}
              {row.status === 'failed' && (
                <Button
                  sx={{
                    fontWeight: 'regular',
                    fontSize: '14px',
                    padding: '4px 10px',
                  }}
                  startIcon={<Refresh />}
                  disabled={
                    currentDownloadItem?.id === row.id ||
                    (isDeleting && row.checked)
                  }
                  onClick={() => handleRetry(row)}
                  variant='outlined'
                  color='primary'
                >
                  {t('downloads.table.row.retry')}
                </Button>
              )}
            </Box>
          );
        },
      },
    ],
    [
      handleDownload,
      handleCancel,
      handleRetry,
      currentDownloadItem,
      isDeleting,
      isMultipleDownloads,
      density,
    ],
  );

  const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
    if (model.type === 'include') {
      setRowsSelection(new Set(Array.from(model.ids).map(id => String(id))));
    } else {
      const excluded = new Set(Array.from(model.ids).map(id => String(id)));
      const allSelectable = rows
        .filter(row => row.status === 'completed' || row.status === 'failed')
        .map(row => row.id)
        .filter(id => !excluded.has(id));
      setRowsSelection(new Set(allSelectable));
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (model.pageSize !== filters.limit) {
      onPageSizeChange({
        target: { value: String(model.pageSize) },
      } as React.ChangeEvent<HTMLInputElement>);
    } else if (model.page !== filters.page - 1) {
      onPageIndexChange(null, model.page);
    }
  };

  return {
    density,
    setDensity,
    resetView,
    columns,
    paginationModel,
    rowSelectionModel,
    handleRowSelectionModelChange,
    handlePaginationModelChange,
  };
};
